import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Menu, X, ArrowRight, Phone } from "lucide-react";
import { CONTACT } from "@/data/site";
import { useMenu } from "@/lib/content";

const container = "max-w-[1240px] mx-auto px-6 lg:px-8";

const NAV_LABELS = {
  "managed-it": "Managed IT",
  cyber: "Cyber Security",
  cloud: "Cloud",
  network: "Network",
  surveillance: "Surveillance",
  connectivity: "Connectivity",
};

const Brand = ({ onClick }) => (
  <Link to="/" onClick={onClick} data-testid="brand-logo" className="flex items-center shrink-0 mr-2">
    <img src="/logo-sm.png" alt="CLI Secure" width="280" height="154" className="h-16 lg:h-[68px] w-auto" />
  </Link>
);

// Flagship product name with `accent` (a substring of the title) in the brand
// gradient — "CLI 360", "Wifinger".
function FlagshipTitle({ title, accent }) {
  const at = accent ? title.indexOf(accent) : -1;
  if (at === -1) return title;
  return (
    <>
      {title.slice(0, at)}
      <span className="bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent">{accent}</span>
      {title.slice(at + accent.length)}
    </>
  );
}

function MegaContent({ entry }) {
  if (entry.layout === "flagship") {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-8 items-start">
        <Link
          to={entry.flagship.href}
          data-testid="mega-flagship-card"
          className="group relative block rounded-2xl bg-slate-900 text-white p-6 overflow-hidden cli-noise"
        >
          <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-purple-900/30 blur-2xl" />
          <span className="inline-block font-cli text-[11px] font-semibold tracking-wider text-slate-900 bg-pink-300 px-2.5 py-1 rounded mb-4">
            {entry.flagship.badge}
          </span>
          <p className="font-heading text-2xl font-bold mb-2">
            <FlagshipTitle title={entry.flagship.title} accent={entry.flagship.accent} />
          </p>
          <p className="text-sm leading-relaxed text-slate-300 mb-4">{entry.flagship.desc}</p>
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-purple-400">
            {entry.flagship.cta} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </span>
        </Link>
        <div>
          <p className="font-cli text-[11px] lowercase tracking-wider text-slate-400 mb-4">› {entry.eyebrow}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {entry.items.map((it) => (
              <MegaTile key={it.title} it={it} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (entry.layout === "columns") {
    return (
      <div>
        <p className="font-cli text-[11px] lowercase tracking-wider text-slate-400 mb-4">› {entry.eyebrow}</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-1">
          {entry.columns.map((col) => (
            <div key={col.head}>
              <p className="font-cli text-[11px] lowercase tracking-wider text-slate-400 px-3 mb-1">{col.head}</p>
              {col.items.map((it) => (
                <MegaTile key={it.title} it={it} compact />
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  const cols = entry.layout === "grid2" ? "sm:grid-cols-2" : "sm:grid-cols-2 lg:grid-cols-3";
  return (
    <div>
      <p className="font-cli text-[11px] lowercase tracking-wider text-slate-400 mb-4">› {entry.eyebrow}</p>
      <div className={`grid grid-cols-1 ${cols} gap-1.5`}>
        {entry.items.map((it) => (
          <MegaTile key={it.title} it={it} />
        ))}
      </div>
    </div>
  );
}

function MegaTile({ it, compact }) {
  return (
    <Link
      to={it.href || "#"}
      data-testid={`mega-link-${it.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}
      className="group block rounded-xl px-3 py-2.5 hover:bg-slate-50 transition-colors"
    >
      <span className="block text-[14px] font-semibold text-slate-900 leading-snug group-hover:text-purple-900 transition-colors">{it.title}</span>
      <span className="block text-[12.5px] text-slate-500 leading-snug">{it.desc}</span>
    </Link>
  );
}

export default function Header() {
  const { menu: MENU, simpleLinks: SIMPLE_LINKS } = useMenu();
  const [openKey, setOpenKey] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const closeTimer = useRef(null);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const openMega = (key) => {
    clearTimeout(closeTimer.current);
    setOpenKey(key);
  };
  const scheduleClose = () => {
    closeTimer.current = setTimeout(() => setOpenKey(null), 130);
  };

  return (
    <>
    <header
      data-testid="main-header"
      className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-slate-200"
      onMouseLeave={scheduleClose}
    >
      {/* Top utility bar (desktop): phone + Contact us live here so the main
          row keeps only the menu and Shop. Mobile keeps them in the drawer. */}
      <div className="hidden lg:block bg-purple-950">
        <div className={`${container} flex items-center justify-end gap-4 h-9`}>
          <a
            href={`tel:${CONTACT.phone.replace(/\s+/g, "")}`}
            data-testid="header-phone"
            className="inline-flex items-center gap-2 whitespace-nowrap text-[13.5px] font-semibold text-white hover:text-pink-200 transition-colors"
          >
            <Phone className="h-3.5 w-3.5 text-pink-300" />
            {CONTACT.phone}
          </a>
          <Link
            to="/contact-us"
            data-testid="header-contact-cta"
            className="inline-flex items-center whitespace-nowrap rounded-full bg-white px-3.5 py-1 text-[12.5px] font-semibold text-purple-950 hover:bg-pink-50 transition-colors"
          >
            Contact us
          </Link>
        </div>
      </div>
      <div className={`${container} flex items-center h-20 gap-1`}>
        <Brand />

        {/* Desktop nav */}
        <nav data-testid="mega-menu" className="hidden lg:flex items-center justify-center gap-0.5 flex-1">
          {MENU.map((entry) => (
            <div
              key={entry.key}
              className="static"
              onMouseEnter={() => openMega(entry.key)}
            >
              <Link
                to={entry.href}
                data-testid={`nav-${entry.key}`}
                className={`flex items-center gap-1 whitespace-nowrap px-3 py-2 rounded-lg text-[14px] font-medium transition-colors ${
                  openKey === entry.key ? "bg-slate-100 text-slate-900" : "text-slate-700 hover:bg-slate-100"
                }`}
              >
                {entry.navLabel || NAV_LABELS[entry.key] || entry.label}
                <ChevronDown className={`h-3.5 w-3.5 opacity-60 transition-transform ${openKey === entry.key ? "rotate-180" : ""}`} />
              </Link>
            </div>
          ))}
          {SIMPLE_LINKS.filter((l) => !/^https?:/.test(l.href)).map((l) => (
            <Link key={l.label} to={l.href} data-testid={`nav-${l.label.toLowerCase()}`}
              className="whitespace-nowrap px-2.5 py-2 rounded-lg text-[14px] font-medium text-slate-700 hover:bg-slate-100 transition-colors">{l.label}</Link>
          ))}
        </nav>

        {/* External simple links (e.g. Shop) sit apart on the right as pink pills. */}
        <div className="hidden lg:flex items-center gap-2">
          {SIMPLE_LINKS.filter((l) => /^https?:/.test(l.href)).map((l) => (
            <a key={l.label} href={l.href} target="_blank" rel="noopener noreferrer" data-testid={`nav-${l.label.toLowerCase()}`}
              className="inline-flex items-center whitespace-nowrap rounded-full bg-pink-500 px-5 py-2 text-[14px] font-semibold text-white hover:bg-pink-600 transition-colors">{l.label}</a>
          ))}
        </div>

        {/* Mobile burger */}
        <button
          data-testid="mobile-menu-toggle"
          aria-label="Open menu"
          onClick={() => setMobileOpen((v) => !v)}
          className="lg:hidden ml-auto p-2 rounded-lg hover:bg-slate-100 text-slate-800"
        >
          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Desktop mega panel */}
      {MENU.map((entry) => (
        <div
          key={entry.key}
          onMouseEnter={() => openMega(entry.key)}
          className={`hidden lg:block absolute left-0 right-0 top-full bg-white border-t border-slate-200 shadow-[0_28px_60px_-30px_rgba(15,23,42,0.35)] transition-all duration-150 ${
            openKey === entry.key ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-2 pointer-events-none"
          }`}
        >
          <div className={`${container} py-7`}>
            <MegaContent entry={entry} />
          </div>
        </div>
      ))}

      {/* Mobile drawer moved outside header below to escape backdrop-filter containing block */}
    </header>
    {mobileOpen && (
      <div data-testid="mobile-drawer" className="lg:hidden fixed inset-x-0 top-16 bottom-0 z-40 bg-white overflow-y-auto border-t border-slate-200">
        <div className="px-4 py-3">
          {MENU.map((entry) => (
            <div key={entry.key} className="border-b border-slate-100">
              <button
                data-testid={`mobile-nav-${entry.key}`}
                onClick={() => setExpanded(expanded === entry.key ? null : entry.key)}
                className="w-full flex items-center justify-between py-4 text-[16px] font-medium text-slate-900"
              >
                {entry.label}
                <ChevronDown className={`h-4 w-4 transition-transform ${expanded === entry.key ? "rotate-180" : ""}`} />
              </button>
              {expanded === entry.key && (
                <div className="pb-3">
                  {entry.layout === "flagship" && (
                    <Link to={entry.flagship.href} onClick={() => setMobileOpen(false)}
                      className="block rounded-xl bg-slate-900 text-white p-4 mb-2">
                      <span className="font-cli text-[10px] font-semibold tracking-wider text-slate-900 bg-pink-300 px-2 py-0.5 rounded">{entry.flagship.badge}</span>
                      <p className="font-heading text-lg font-bold mt-2"><FlagshipTitle title={entry.flagship.title} accent={entry.flagship.accent} /></p>
                      <p className="text-xs text-slate-300 mt-1">{entry.flagship.desc}</p>
                    </Link>
                  )}
                  {(entry.columns
                    ? entry.columns.flatMap((c) => c.items)
                    : entry.items
                  ).map((it) => (
                    <Link key={it.title} to={it.href || "#"} onClick={() => setMobileOpen(false)}
                      className="block py-2.5 pl-1">
                      <span className="block text-[14px] font-semibold text-slate-800">{it.title}</span>
                      <span className="block text-[12px] text-slate-500">{it.desc}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
          {SIMPLE_LINKS.map((l) => {
            const cls = "block py-4 text-[16px] font-medium text-slate-900 border-b border-slate-100";
            return /^https?:/.test(l.href)
              ? <a key={l.label} href={l.href} target="_blank" rel="noopener noreferrer" onClick={() => setMobileOpen(false)}
                  className="mt-3 inline-flex items-center justify-center rounded-lg bg-pink-500 px-5 py-3 text-[15px] font-semibold text-white">{l.label}</a>
              : <Link key={l.label} to={l.href} onClick={() => setMobileOpen(false)} className={cls}>{l.label}</Link>;
          })}
          <a href={`tel:${CONTACT.phone.replace(/\s+/g, "")}`} data-testid="mobile-phone"
            className="flex items-center gap-2 py-4 text-[16px] font-semibold text-slate-900 border-b border-slate-100">
            <Phone className="h-4 w-4 text-purple-900" /> {CONTACT.phone}
          </a>
          <Link to="/contact-us" onClick={() => setMobileOpen(false)}
            className="block text-center mt-4 rounded-lg bg-purple-900 px-4 py-3.5 text-[15px] font-semibold text-white">
            Contact us
          </Link>
        </div>
      </div>
    )}
    </>
  );
}
